import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { BASE_API_URL } from "../config";

/**
 * Keeps the user signed in.
 *
 * The access token Supabase issues is short-lived. The refresh token that
 * comes with it is what lets us swap it for a new one, so as long as we have
 * that we never need to send anyone back to the login page. The token is
 * refreshed a little before it runs out — on a timer, whenever the tab comes
 * back into view, and just before any request that would carry a stale one —
 * and a request that still comes back 401 is refreshed and retried once.
 *
 * Call sites keep reading `localStorage.accessToken` exactly as before; this
 * module is what makes sure the value there is current.
 */

// Refresh when the access token has less than this long left.
const REFRESH_MARGIN_S = 5 * 60;
const CHECK_EVERY_MS = 60 * 1000;
const REFRESH_URL = `${BASE_API_URL}/api/v1/auth/refresh`;

const SESSION_KEYS = [
  "accessToken",
  "tokenType",
  "refreshToken",
  "userGuid",
  "userType",
  "subscriptionPlanGuid",
  "firstLetter",
];

export function saveSession({ access_token, token_type, refresh_token }) {
  localStorage.setItem("accessToken", access_token);
  localStorage.setItem("tokenType", token_type || "bearer");
  if (refresh_token) localStorage.setItem("refreshToken", refresh_token);
}

export function clearSession() {
  SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
}

function claims(token) {
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
}

export function isTokenExpired(token, marginS = 0) {
  const exp = token && claims(token)?.exp;
  return !exp || exp - marginS < Date.now() / 1000;
}

// Due a refresh: inside the margin, or past half its life for a token issued
// for less than twice the margin, so a short-lived one isn't refreshed on
// every request.
function isDueRefresh(token) {
  const c = claims(token);
  if (!c?.exp) return true;
  const margin = c.iat ? Math.min(REFRESH_MARGIN_S, (c.exp - c.iat) / 2) : REFRESH_MARGIN_S;
  return isTokenExpired(token, margin);
}

/** Whether a session can still be carried on, now or after a refresh. */
export function hasSession() {
  return (
    !isTokenExpired(localStorage.getItem("accessToken")) ||
    !!localStorage.getItem("refreshToken")
  );
}

// The native fetch, before installAuthRefresh wraps it, so the refresh call
// itself never goes back through the wrapper.
const rawFetch = window.fetch.bind(window);

async function exchange() {
  // Another tab may have refreshed while we waited for the lock.
  const current = localStorage.getItem("accessToken");
  if (current && !isDueRefresh(current)) return current;

  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  let res;
  try {
    res = await rawFetch(REFRESH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    return null; // offline: keep the session and try again later
  }

  if (res.ok) {
    const session = await res.json();
    // Signed out (or signed in afresh) while this was in flight: that session
    // is gone, and writing this one back would sign them in again.
    if (localStorage.getItem("refreshToken") !== refreshToken) return null;
    saveSession(session);
    return localStorage.getItem("accessToken");
  }
  // Only a definite no ends the session. A 5xx is the API or Supabase having
  // a moment, and the refresh token is still good for the next try.
  if (res.status === 401 && localStorage.getItem("refreshToken") === refreshToken) {
    clearSession();
  }
  return null;
}

let inFlight = null;

/**
 * Swap the refresh token for a new session. Resolves to the new access token,
 * or null if there isn't one. Supabase refresh tokens are single use, so tabs
 * take turns through a lock rather than racing each other with the same one.
 */
export function refreshSession() {
  if (!inFlight) {
    const run = navigator.locks
      ? navigator.locks.request("stande-session-refresh", exchange)
      : exchange();
    inFlight = run.finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** The current access token, refreshed first if it is about to run out. */
export async function getFreshAccessToken() {
  const token = localStorage.getItem("accessToken");
  if (token && !isDueRefresh(token)) return token;
  if (!localStorage.getItem("refreshToken")) return token;
  return (await refreshSession()) || token;
}

// What `Bearer ${localStorage.getItem("accessToken")}` comes out as when it
// was read while no token was stored.
const PLACEHOLDER_TOKENS = new Set(["null", "undefined", ""]);

/**
 * The token in an Authorization header, if it is this user's session token —
 * the current one, an older one a component held on to from before a refresh,
 * or a placeholder ("Bearer null") read while the session was briefly absent.
 * Those are swapped for the current token. Anything else is left alone: a
 * newer token than the stored one (just issued at login and not stored yet),
 * another service's credential, or a placeholder when nobody is signed in.
 */
function sessionTokenIn(authorization) {
  const match = /^bearer\s*(.*)$/i.exec(authorization || "");
  if (!match) return null;
  const sent = match[1].trim();
  const stored = localStorage.getItem("accessToken");
  if (!stored) return null;
  if (sent === stored || PLACEHOLDER_TOKENS.has(sent)) return sent;
  const s = claims(sent);
  const c = claims(stored);
  const olderOfSameUser = s?.sub && s.sub === c?.sub && s.exp && c.exp && s.exp < c.exp;
  return olderOfSameUser ? sent : null;
}

function installAxios() {
  axios.interceptors.request.use(async (config) => {
    const auth = config.headers?.get?.("Authorization") ?? config.headers?.Authorization;
    const sent = sessionTokenIn(auth);
    if (sent) {
      const fresh = await getFreshAccessToken();
      if (fresh && fresh !== sent) config.headers.set("Authorization", `Bearer ${fresh}`);
    }
    return config;
  });

  axios.interceptors.response.use(undefined, async (error) => {
    const config = error.config;
    if (error.response?.status !== 401 || !config || config._stndRetried) throw error;
    const sent = sessionTokenIn(config.headers?.get?.("Authorization"));
    if (!sent) throw error;
    const fresh = await refreshSession();
    if (!fresh || fresh === sent) throw error;
    config._stndRetried = true;
    config.headers.set("Authorization", `Bearer ${fresh}`);
    return axios(config);
  });
}

function installFetch() {
  window.fetch = async (input, init) => {
    // Only the (url, options) form, which is the one this app uses. A Request
    // object's body can't be replayed, so it goes through untouched.
    if (input instanceof Request || !init?.headers) return rawFetch(input, init);

    const headers = new Headers(init.headers);
    const sent = sessionTokenIn(headers.get("Authorization"));
    if (!sent) return rawFetch(input, init);

    const send = (token) => {
      headers.set("Authorization", `Bearer ${token}`);
      return rawFetch(input, { ...init, headers });
    };

    const fresh = (await getFreshAccessToken()) || sent;
    const res = await send(fresh);
    if (res.status !== 401) return res;

    const retry = await refreshSession();
    return retry && retry !== fresh ? send(retry) : res;
  };
}

let installed = false;

export function installAuthRefresh() {
  if (installed) return;
  installed = true;
  installAxios();
  installFetch();

  const topUp = () => {
    if (localStorage.getItem("refreshToken")) getFreshAccessToken();
  };
  // Timers stall while a laptop sleeps, so coming back to the tab or back
  // online counts as a reason to check too.
  setInterval(topUp, CHECK_EVERY_MS);
  window.addEventListener("focus", topUp);
  window.addEventListener("online", topUp);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") topUp();
  });
  topUp();
}
